// src/init.ts
import {
  init as coreInit,
  createFetchExecutor
} from "@hopdrive/sdk-core";

// src/geo/types/common.ts
var GeoError = class extends Error {
  constructor(message, code, details) {
    super(message);
    this.code = code;
    this.details = details;
    this.name = "GeoError";
  }
};
var GeocodingError = class extends GeoError {
  constructor(message, code, address, details) {
    super(message, code, details);
    this.address = address;
    this.name = "GeocodingError";
  }
};
var RoutingError = class extends GeoError {
  constructor(message, code, origin, destination, details) {
    super(message, code, details);
    this.origin = origin;
    this.destination = destination;
    this.name = "RoutingError";
  }
};
var SuggestionError = class extends GeoError {
  constructor(message, code, input, details) {
    super(message, code, details);
    this.input = input;
    this.name = "SuggestionError";
  }
};
var GeoApiError = class extends GeoError {
  constructor(message, statusCode, response) {
    super(message, `HTTP_${statusCode}`);
    this.statusCode = statusCode;
    this.response = response;
    this.name = "GeoApiError";
  }
};
var GeoRateLimitError = class extends GeoError {
  constructor(message, retryAfterMs) {
    super(message, "RATE_LIMITED");
    this.retryAfterMs = retryAfterMs;
    this.name = "GeoRateLimitError";
  }
};

// src/geo/providers/google/client.ts
import { getLogger } from "@hopdrive/sdk-core";
var RETRYABLE_STATUS_CODES = /* @__PURE__ */ new Set([
  408,
  // Request Timeout
  429,
  // Too Many Requests
  500,
  // Internal Server Error
  502,
  // Bad Gateway
  503,
  // Service Unavailable
  504
  // Gateway Timeout
]);
var GoogleClient = class {
  constructor(config) {
    if (!config.apiKey) {
      throw new Error("Google API key is required");
    }
    this.apiKey = config.apiKey;
    this.timeout = config.timeout ?? 1e4;
    this.maxRetries = config.maxRetries ?? 3;
    this.baseDelayMs = config.baseDelayMs ?? 200;
    this.maxDelayMs = config.maxDelayMs ?? 5e3;
  }
  /**
   * Make a GET request to a Google API endpoint
   */
  async get(url, options) {
    return this.request(url, { ...options, method: "GET" });
  }
  /**
   * Make a POST request to a Google API endpoint
   */
  async post(url, body, options) {
    return this.request(url, {
      ...options,
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        ...options?.headers
      }
    });
  }
  /**
   * Make an HTTP request with retry logic
   */
  async request(url, options) {
    const urlWithParams = this.buildUrl(url, options?.queryParams);
    const requestTimeout = options?.timeout ?? this.timeout;
    const maxAttempts = options?.noRetry ? 1 : this.maxRetries + 1;
    let lastError;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const result = await this.executeRequest(
          urlWithParams,
          options,
          requestTimeout
        );
        return result;
      } catch (error) {
        lastError = error;
        if (attempt >= maxAttempts - 1 || !this.isRetryable(error)) {
          throw error;
        }
        const delay = this.calculateBackoff(attempt);
        const log = options?.logger ?? getLogger();
        log.warn("googleClient.retry", {
          attempt: attempt + 1,
          maxAttempts,
          delayMs: Math.round(delay),
          statusCode: lastError instanceof GeoApiError ? lastError.statusCode : void 0,
          errorMessage: lastError.message
        });
        await this.sleep(delay);
      }
    }
    throw lastError ?? new GeoError("Request failed after all retries");
  }
  async executeRequest(url, options, timeout) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      const { queryParams, timeout: _timeout, noRetry, logger: _logger, ...fetchOptions } = options ?? {};
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal
      });
      if (!response.ok) {
        await this.handleErrorResponse(response);
      }
      const text = await response.text();
      if (!text) {
        return {};
      }
      return JSON.parse(text);
    } catch (error) {
      if (error instanceof GeoError) {
        throw error;
      }
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new GeoApiError(`Request timeout after ${timeout}ms`, 408);
        }
        throw new GeoApiError(
          `Network error: ${error.message}`,
          0,
          { cause: error.message }
        );
      }
      throw new GeoError("Unknown error during request");
    } finally {
      clearTimeout(timeoutId);
    }
  }
  async handleErrorResponse(response) {
    let errorData;
    try {
      const text = await response.text();
      if (text) {
        errorData = JSON.parse(text);
      }
    } catch {
    }
    const errorMessage = errorData?.error?.message ?? response.statusText ?? "Request failed";
    if (response.status === 429) {
      const retryAfter = response.headers.get("Retry-After");
      const retryAfterMs = retryAfter ? parseInt(retryAfter, 10) * 1e3 : void 0;
      throw new GeoRateLimitError(
        `Rate limit exceeded: ${errorMessage}`,
        retryAfterMs
      );
    }
    throw new GeoApiError(errorMessage, response.status, errorData);
  }
  buildUrl(baseUrl, queryParams) {
    const url = new URL(baseUrl);
    url.searchParams.set("key", this.apiKey);
    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== void 0) {
          url.searchParams.set(key, value);
        }
      }
    }
    return url.toString();
  }
  isRetryable(error) {
    if (error instanceof GeoRateLimitError) {
      return true;
    }
    if (error instanceof GeoApiError) {
      return RETRYABLE_STATUS_CODES.has(error.statusCode);
    }
    if (error instanceof Error) {
      return error.name === "AbortError" || error.message.includes("network") || error.message.includes("ECONNRESET") || error.message.includes("ETIMEDOUT");
    }
    return false;
  }
  calculateBackoff(attempt) {
    const exponentialDelay = this.baseDelayMs * Math.pow(2, attempt);
    const jitter = Math.random() * exponentialDelay * 0.25;
    return Math.min(exponentialDelay + jitter, this.maxDelayMs);
  }
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
};

// src/geo/providers/google/geocoding.ts
import { OpenLocationCode } from "open-location-code";
var plusCodeGenerator = new OpenLocationCode();
var GEOCODING_API_URL = "https://maps.googleapis.com/maps/api/geocode/json";
var PLACE_DETAILS_API_URL = "https://places.googleapis.com/v1/places";
var PLACE_DETAILS_FIELD_MASK = [
  "id",
  "formattedAddress",
  "location",
  "addressComponents",
  "plusCode"
].join(",");
var PLACES_SEARCH_TEXT_URL = "https://places.googleapis.com/v1/places:searchText";
var PLACES_SEARCH_TEXT_FIELD_MASK = [
  "places.id",
  "places.displayName",
  "places.formattedAddress",
  "places.addressComponents",
  "places.location",
  "places.plusCode",
  "places.types"
].join(",");
var COMPONENT_TYPE_MAP = {
  street_number: "streetNumber",
  route: "route",
  locality: "city",
  sublocality: "city",
  // fallback for city
  sublocality_level_1: "city",
  // another fallback
  administrative_area_level_2: "county",
  administrative_area_level_1: "state",
  postal_code: "postalCode",
  country: "country",
  neighborhood: "neighborhood",
  subpremise: "subpremise"
};
var GoogleGeocodingProvider = class {
  constructor(client) {
    this.name = "google_geocoding";
    this.client = client;
  }
  /**
   * Resolve a Place ID to full geocode data using Places API (New).
   *
   * Unlike the legacy Geocoding API, the Places API (New) works with
   * browser API keys that have HTTP referer restrictions, making this
   * suitable for frontend usage.
   */
  async fetchPlaceDetails(placeId, options) {
    const url = `${PLACE_DETAILS_API_URL}/${placeId}`;
    const response = await this.client.get(
      url,
      {
        headers: {
          "X-Goog-FieldMask": PLACE_DETAILS_FIELD_MASK
        },
        ...options?.logger && { logger: options.logger }
      }
    );
    if (response.error) {
      throw new GeocodingError(
        response.error.message,
        response.error.status,
        placeId
      );
    }
    if (!response.location) {
      throw new GeocodingError(
        "Place details response missing location",
        "MISSING_LOCATION",
        placeId
      );
    }
    return this.mapPlaceDetailsToCanonical(response, placeId);
  }
  /**
   * Convert an address string to coordinates.
   *
   * Uses the **CORS-safe** Places API (New) `places:searchText` endpoint
   * (`places.googleapis.com/v1/places:searchText`). Suitable for browser
   * runtimes — browser API keys with HTTP referer restrictions work here.
   *
   * The legacy `maps.googleapis.com/maps/api/geocode/json` endpoint is
   * **not** used by this method any more; that endpoint returns no CORS
   * headers and crashes from browser origins.
   */
  async geocode(request) {
    return this.searchByText(request);
  }
  async searchByText(request) {
    const body = { textQuery: request.address };
    if (request.regionBias) {
      body.regionCode = request.regionBias;
    }
    if (request.bounds) {
      body.locationBias = {
        rectangle: {
          low: {
            latitude: request.bounds.south,
            longitude: request.bounds.west
          },
          high: {
            latitude: request.bounds.north,
            longitude: request.bounds.east
          }
        }
      };
    }
    if (request.language) {
      body.languageCode = request.language;
    }
    const response = await this.client.post(
      PLACES_SEARCH_TEXT_URL,
      body,
      {
        headers: {
          "X-Goog-FieldMask": PLACES_SEARCH_TEXT_FIELD_MASK
        },
        ...request.logger && { logger: request.logger }
      }
    );
    if (response.error) {
      throw new GeocodingError(
        response.error.message,
        response.error.status,
        request.address
      );
    }
    const place = response.places?.[0];
    if (!place) {
      throw new GeocodingError(
        "No places matched the query",
        "ZERO_RESULTS",
        request.address
      );
    }
    return this.mapSearchTextPlaceToCanonical(place, request.address);
  }
  /**
   * Reverse-geocode coordinates to an address.
   *
   * **Server-only.** This method calls `maps.googleapis.com/maps/api/geocode/json`,
   * which is part of Google's legacy Maps Web Services family and does not return
   * CORS headers for browser origins. Browser callers will receive a network/CORS
   * error. If you need lat/lng → address resolution from the browser, route the
   * call through your own backend.
   *
   * Places API (New) does not have a direct CORS-safe equivalent for arbitrary
   * coordinates; `places:searchNearby` returns nearby places, not the canonical
   * address at a point. If that approximation is acceptable for your use case,
   * implement a custom provider.
   */
  async reverseGeocode(location) {
    const queryParams = {
      latlng: `${location.latitude},${location.longitude}`
    };
    const response = await this.client.get(
      GEOCODING_API_URL,
      { queryParams }
    );
    this.validateResponse(
      response,
      `${location.latitude},${location.longitude}`
    );
    const result = response.results[0];
    const components = this.extractComponents(result.address_components);
    return {
      formattedAddress: result.formatted_address,
      placeId: result.place_id,
      components,
      source: this.name,
      geocodedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
  validateResponse(response, address) {
    const { status, error_message } = response;
    switch (status) {
      case "OK":
        if (response.results.length === 0) {
          throw new GeocodingError(
            "No results returned",
            "ZERO_RESULTS",
            address
          );
        }
        return;
      case "ZERO_RESULTS":
        throw new GeocodingError(
          "No results found for address",
          "ZERO_RESULTS",
          address
        );
      case "OVER_DAILY_LIMIT":
      case "OVER_QUERY_LIMIT":
        throw new GeocodingError(
          error_message ?? "API quota exceeded",
          status,
          address,
          { retryable: true }
        );
      case "REQUEST_DENIED":
        throw new GeocodingError(
          error_message ?? "Request denied - check API key",
          "REQUEST_DENIED",
          address
        );
      case "INVALID_REQUEST":
        throw new GeocodingError(
          error_message ?? "Invalid request - check address format",
          "INVALID_REQUEST",
          address
        );
      default:
        throw new GeocodingError(
          error_message ?? `Geocoding failed: ${status}`,
          status,
          address
        );
    }
  }
  extractComponents(addressComponents) {
    const components = {};
    for (const component of addressComponents) {
      for (const type of component.types) {
        const mappedField = COMPONENT_TYPE_MAP[type];
        if (mappedField && !components[mappedField]) {
          components[mappedField] = component.long_name;
          if (type === "administrative_area_level_1") {
            components.stateCode = component.short_name;
          }
          if (type === "country") {
            components.countryCode = component.short_name;
          }
        }
      }
    }
    return components;
  }
  mapSearchTextPlaceToCanonical(place, addressInput) {
    const lat = place.location?.latitude;
    const lng = place.location?.longitude;
    if (lat == null || lng == null) {
      throw new GeocodingError(
        "Place missing location coordinates",
        "MISSING_LOCATION",
        addressInput
      );
    }
    const components = this.extractPlaceDetailsComponents(
      place.addressComponents ?? []
    );
    const streetAddress = this.buildStreetAddressFromComponents(
      components,
      place.formattedAddress ?? ""
    );
    const plusCode = place.plusCode?.globalCode ?? plusCodeGenerator.encode(lat, lng);
    const locationType = this.deriveLocationTypeFromTypes(place.types ?? []);
    return {
      streetAddress,
      formattedAddress: place.formattedAddress ?? "",
      latitude: lat,
      longitude: lng,
      placeId: place.id,
      plusCode,
      locationType,
      components,
      source: "google_places_search_text",
      geocodedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
  deriveLocationTypeFromTypes(types) {
    if (types.includes("street_address") || types.includes("premise") || types.includes("subpremise")) {
      return "ROOFTOP";
    }
    if (types.includes("route")) {
      return "GEOMETRIC_CENTER";
    }
    return "APPROXIMATE";
  }
  mapPlaceDetailsToCanonical(response, placeId) {
    const lat = response.location.latitude;
    const lng = response.location.longitude;
    const components = this.extractPlaceDetailsComponents(response.addressComponents ?? []);
    const streetAddress = this.buildStreetAddressFromComponents(components, response.formattedAddress ?? "");
    const plusCode = response.plusCode?.globalCode ?? plusCodeGenerator.encode(lat, lng);
    return {
      streetAddress,
      formattedAddress: response.formattedAddress ?? "",
      latitude: lat,
      longitude: lng,
      placeId,
      plusCode,
      locationType: "ROOFTOP",
      components,
      source: "google_place_details",
      geocodedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
  extractPlaceDetailsComponents(addressComponents) {
    const components = {};
    for (const component of addressComponents) {
      for (const type of component.types) {
        const mappedField = COMPONENT_TYPE_MAP[type];
        if (mappedField && !components[mappedField]) {
          components[mappedField] = component.longText;
          if (type === "administrative_area_level_1") {
            components.stateCode = component.shortText;
          }
          if (type === "country") {
            components.countryCode = component.shortText;
          }
        }
      }
    }
    return components;
  }
  buildStreetAddressFromComponents(components, formattedAddress) {
    if (components.streetNumber && components.route) {
      return `${components.streetNumber} ${components.route}`;
    }
    const firstPart = formattedAddress.split(",")[0];
    return firstPart?.trim() ?? formattedAddress;
  }
};

// src/geo/providers/google/routes.ts
var ROUTES_API_URL = "https://routes.googleapis.com/directions/v2:computeRoutes";
var DISTANCE_MATRIX_API_URL = "https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix";
var GoogleRoutesProvider = class {
  constructor(client) {
    this.name = "google_routes";
    this.client = client;
  }
  async computeRoute(request) {
    const body = this.buildRequestBody(request);
    const fieldMask = this.buildFieldMask(request);
    const response = await this.client.post(
      ROUTES_API_URL,
      body,
      {
        headers: {
          "X-Goog-FieldMask": fieldMask
        },
        ...request.logger && { logger: request.logger }
      }
    );
    this.validateResponse(response, request);
    return this.mapToCanonical(response.routes[0], request);
  }
  async computeDistanceMatrix(request) {
    const body = this.buildMatrixRequestBody(request);
    const responses = await this.client.post(
      DISTANCE_MATRIX_API_URL,
      body,
      {
        headers: {
          "X-Goog-FieldMask": "originIndex,destinationIndex,distanceMeters,duration,staticDuration,status,condition"
        }
      }
    );
    return this.mapMatrixToCanonical(
      responses,
      request.origins.length,
      request.destinations.length
    );
  }
  buildRequestBody(request) {
    const body = {
      origin: this.buildWaypoint(request.origin),
      destination: this.buildWaypoint(request.destination),
      travelMode: this.mapTravelMode(request.travelMode)
    };
    if (request.waypoints?.length) {
      body.intermediates = request.waypoints.map((wp) => this.buildWaypoint(wp));
    }
    body.routingPreference = this.getRoutingPreference(request);
    const modifiers = this.buildRouteModifiers(request);
    if (Object.keys(modifiers).length > 0) {
      body.routeModifiers = modifiers;
    }
    if (request.departureTime) {
      body.departureTime = typeof request.departureTime === "string" ? request.departureTime : request.departureTime.toISOString();
    }
    if (request.includeTolls) {
      body.extraComputations = ["TOLLS"];
    }
    return body;
  }
  buildWaypoint(location) {
    return {
      location: {
        latLng: {
          latitude: location.latitude,
          longitude: location.longitude
        }
      }
    };
  }
  mapTravelMode(mode) {
    switch (mode) {
      case "WALK":
        return "WALK";
      case "BICYCLE":
        return "BICYCLE";
      case "TWO_WHEELER":
        return "TWO_WHEELER";
      default:
        return "DRIVE";
    }
  }
  getRoutingPreference(request) {
    if (request.routePreference === "SHORTEST") {
      return "TRAFFIC_UNAWARE";
    }
    if (request.departureTime) {
      return "TRAFFIC_AWARE_OPTIMAL";
    }
    return "TRAFFIC_UNAWARE";
  }
  buildRouteModifiers(request) {
    const modifiers = {};
    if (request.avoidTolls) {
      modifiers.avoidTolls = true;
    }
    if (request.avoidHighways) {
      modifiers.avoidHighways = true;
    }
    if (request.avoidFerries) {
      modifiers.avoidFerries = true;
    }
    if (request.vehicleInfo?.emissionType) {
      modifiers.vehicleInfo = {
        emissionType: request.vehicleInfo.emissionType
      };
    }
    if (request.vehicleInfo?.hasTollTransponder) {
      modifiers.tollPasses = ["US_CA_FASTRAK", "US_TX_TXTAG", "US_MA_EZPASSMA"];
    }
    return modifiers;
  }
  buildFieldMask(request) {
    const fields = [
      "routes.distanceMeters",
      "routes.duration",
      "routes.staticDuration",
      "routes.description",
      "routes.warnings"
    ];
    if (request.includePolyline) {
      fields.push("routes.polyline.encodedPolyline");
    }
    if (request.includeTolls) {
      fields.push("routes.travelAdvisory.tollInfo");
    }
    if (request.includeSteps) {
      fields.push("routes.legs.steps.navigationInstruction");
      fields.push("routes.legs.steps.distanceMeters");
      fields.push("routes.legs.steps.staticDuration");
      fields.push("routes.legs.steps.polyline.encodedPolyline");
    }
    if (request.waypoints?.length || request.includeSteps) {
      fields.push("routes.legs.distanceMeters");
      fields.push("routes.legs.duration");
      fields.push("routes.legs.staticDuration");
      fields.push("routes.legs.startLocation");
      fields.push("routes.legs.endLocation");
    }
    return fields.join(",");
  }
  validateResponse(response, request) {
    if (response.error) {
      throw new RoutingError(
        response.error.message,
        response.error.status,
        request.origin,
        request.destination,
        { googleError: response.error }
      );
    }
    if (!response.routes?.length) {
      const fallbackReason = response.fallbackInfo?.reason;
      throw new RoutingError(
        fallbackReason ? `No route found: ${fallbackReason}` : "No route found between the specified locations",
        "NO_ROUTES",
        request.origin,
        request.destination
      );
    }
  }
  mapToCanonical(route, request) {
    const result = {
      distanceMeters: route.distanceMeters,
      durationSeconds: this.parseDuration(route.duration),
      source: this.name,
      computedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
    if (route.staticDuration) {
      result.staticDurationSeconds = this.parseDuration(route.staticDuration);
    }
    if (route.polyline?.encodedPolyline) {
      result.polyline = route.polyline.encodedPolyline;
    }
    if (route.description) {
      result.routeSummary = [route.description];
    }
    if (route.warnings?.length) {
      result.warnings = route.warnings;
    }
    if (route.travelAdvisory?.tollInfo) {
      result.tolls = this.extractTolls(route.travelAdvisory.tollInfo);
    }
    if (route.legs?.length) {
      result.legs = route.legs.map((leg) => this.mapLeg(leg));
    }
    if (request.includeSteps && route.legs?.[0]?.steps) {
      result.steps = route.legs.flatMap(
        (leg) => leg.steps?.map((step) => this.mapStep(step)) ?? []
      );
    }
    return result;
  }
  parseDuration(duration) {
    const match = duration.match(/^(\d+(?:\.\d+)?)s$/);
    if (!match) {
      throw new RoutingError(`Invalid duration format: ${duration}`, "PARSE_ERROR");
    }
    return Math.round(parseFloat(match[1]));
  }
  extractTolls(tollInfo) {
    const prices = tollInfo.estimatedPrice ?? [];
    if (prices.length === 0) {
      return {
        amountCents: 0,
        currency: "USD",
        isComplete: true
      };
    }
    const breakdown = [];
    let totalCents = 0;
    let primaryCurrency = "USD";
    let hasMultipleCurrencies = false;
    for (const price of prices) {
      const cents = this.moneyToCents(price);
      totalCents += cents;
      primaryCurrency = price.currencyCode;
      if (prices.indexOf(price) > 0 && price.currencyCode !== prices[0].currencyCode) {
        hasMultipleCurrencies = true;
      }
      breakdown.push({
        name: "Toll",
        amountCents: cents,
        currency: price.currencyCode
      });
    }
    return {
      amountCents: totalCents,
      currency: primaryCurrency,
      isComplete: !hasMultipleCurrencies,
      breakdown: breakdown.length > 1 ? breakdown : void 0
    };
  }
  moneyToCents(money) {
    const units = parseInt(money.units ?? "0", 10);
    const nanos = money.nanos ?? 0;
    return Math.round(units * 100 + nanos / 1e7);
  }
  mapLeg(leg) {
    const result = {
      distanceMeters: leg.distanceMeters ?? 0,
      durationSeconds: leg.duration ? this.parseDuration(leg.duration) : 0,
      startLocation: leg.startLocation?.latLng ?? { latitude: 0, longitude: 0 },
      endLocation: leg.endLocation?.latLng ?? { latitude: 0, longitude: 0 }
    };
    if (leg.staticDuration) {
      result.staticDurationSeconds = this.parseDuration(leg.staticDuration);
    }
    if (leg.steps) {
      result.steps = leg.steps.map((step) => this.mapStep(step));
    }
    return result;
  }
  mapStep(step) {
    const result = {
      instruction: step.navigationInstruction?.instructions ?? "Continue on route",
      distanceMeters: step.distanceMeters,
      durationSeconds: step.staticDuration ? this.parseDuration(step.staticDuration) : 0
    };
    if (step.polyline?.encodedPolyline) {
      result.polyline = step.polyline.encodedPolyline;
    }
    if (step.navigationInstruction?.maneuver) {
      result.maneuver = step.navigationInstruction.maneuver;
    }
    return result;
  }
  buildMatrixRequestBody(request) {
    const routeModifiers = request.avoidTolls || request.avoidHighways ? { avoidTolls: request.avoidTolls, avoidHighways: request.avoidHighways } : void 0;
    return {
      origins: request.origins.map((o) => ({
        waypoint: this.buildWaypoint(o),
        ...routeModifiers && { routeModifiers }
      })),
      destinations: request.destinations.map((d) => ({
        waypoint: this.buildWaypoint(d)
      })),
      travelMode: this.mapTravelMode(request.travelMode),
      routingPreference: request.departureTime ? "TRAFFIC_AWARE" : "TRAFFIC_UNAWARE",
      departureTime: request.departureTime ? typeof request.departureTime === "string" ? request.departureTime : request.departureTime.toISOString() : void 0
    };
  }
  mapMatrixToCanonical(responses, originCount, destinationCount) {
    const rows = [];
    for (let i = 0; i < originCount; i++) {
      rows.push({
        elements: new Array(destinationCount).fill(null).map(
          () => ({
            distanceMeters: 0,
            durationSeconds: 0,
            status: "NOT_FOUND"
          })
        )
      });
    }
    for (const response of responses) {
      const entry = {
        distanceMeters: response.distanceMeters ?? 0,
        durationSeconds: response.duration ? this.parseDuration(response.duration) : 0,
        staticDurationSeconds: response.staticDuration ? this.parseDuration(response.staticDuration) : void 0,
        status: response.condition === "ROUTE_EXISTS" ? "OK" : response.condition === "ROUTE_NOT_FOUND" ? "NOT_FOUND" : "ZERO_RESULTS"
      };
      rows[response.originIndex].elements[response.destinationIndex] = entry;
    }
    return {
      rows,
      source: this.name,
      computedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
};

// src/geo/providers/google/suggestion.ts
var AUTOCOMPLETE_API_URL = "https://places.googleapis.com/v1/places:autocomplete";
var FIELD_MASK = [
  "suggestions.placePrediction.placeId",
  "suggestions.placePrediction.text",
  "suggestions.placePrediction.structuredFormat",
  "suggestions.placePrediction.types",
  "suggestions.placePrediction.distanceMeters"
].join(",");
var GoogleSuggestionProvider = class {
  constructor(client) {
    this.name = "google_places_autocomplete";
    this.client = client;
  }
  async suggest(request) {
    const body = this.buildRequestBody(request);
    const response = await this.client.post(
      AUTOCOMPLETE_API_URL,
      body,
      {
        headers: {
          "X-Goog-FieldMask": FIELD_MASK
        },
        ...request.logger && { logger: request.logger }
      }
    );
    this.validateResponse(response, request.input);
    return {
      predictions: (response.suggestions ?? []).map(
        (s) => this.mapToCanonical(s)
      ),
      source: this.name,
      suggestedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
  buildRequestBody(request) {
    const body = {
      input: request.input
    };
    if (request.sessionToken) {
      body.sessionToken = request.sessionToken;
    }
    if (request.locationBias) {
      body.locationBias = {
        circle: {
          center: {
            latitude: request.locationBias.center.latitude,
            longitude: request.locationBias.center.longitude
          },
          radius: request.locationBias.radiusMeters ?? 5e4
        }
      };
    }
    if (request.includedTypes?.length) {
      body.includedPrimaryTypes = request.includedTypes;
    }
    if (request.includedRegionCodes?.length) {
      body.includedRegionCodes = request.includedRegionCodes;
    }
    if (request.language) {
      body.languageCode = request.language;
    }
    return body;
  }
  validateResponse(response, input) {
    if (response.error) {
      throw new SuggestionError(
        response.error.message,
        response.error.status,
        input,
        { googleError: response.error }
      );
    }
  }
  mapToCanonical(suggestion) {
    const { placePrediction: p } = suggestion;
    const mainText = p.structuredFormat?.mainText?.text ?? "";
    const secondaryText = p.structuredFormat?.secondaryText?.text ?? "";
    const fullText = p.text?.text ?? [mainText, secondaryText].filter(Boolean).join(", ");
    return {
      placeId: p.placeId,
      mainText,
      secondaryText,
      fullText,
      types: p.types ?? [],
      distanceMeters: p.distanceMeters
    };
  }
};

// src/geo/providers/google/timezone.ts
import { getLogger as getLogger2 } from "@hopdrive/sdk-core";
var TIMEZONE_API_URL = "https://maps.googleapis.com/maps/api/timezone/json";
var GoogleTimezoneProvider = class {
  constructor(client) {
    this.name = "google_timezone";
    this.client = client;
  }
  /**
   * Resolve timezone for the given coordinates.
   *
   * Returns null (rather than throwing) on failure because timezone
   * is enrichment data — callers should not fail if it's unavailable.
   */
  async resolveTimezone(location, options) {
    const log = options?.logger ?? getLogger2();
    try {
      const timestamp = Math.floor(Date.now() / 1e3);
      const response = await this.client.get(
        TIMEZONE_API_URL,
        {
          queryParams: {
            location: `${location.latitude},${location.longitude}`,
            timestamp: String(timestamp)
          },
          ...options?.logger && { logger: options.logger }
        }
      );
      if (response.status !== "OK" || !response.timeZoneId) {
        log.warn("timezone.resolutionFailed", {
          status: response.status,
          errorMessage: response.errorMessage,
          latitude: location.latitude,
          longitude: location.longitude
        });
        return null;
      }
      return {
        timeZoneId: response.timeZoneId,
        timeZoneName: response.timeZoneName ?? "",
        rawOffsetSeconds: response.rawOffset ?? 0,
        dstOffsetSeconds: response.dstOffset ?? 0
      };
    } catch (error) {
      log.warn("timezone.resolutionError", {
        error,
        latitude: location.latitude,
        longitude: location.longitude
      });
      return null;
    }
  }
};
async function resolveTimezone(lat, lng, options) {
  const { getDefaultGeoProviders: getDefaultGeoProviders2 } = await import("./init-WBSDPMB5.mjs");
  const { timezone } = getDefaultGeoProviders2();
  const location = { latitude: lat, longitude: lng };
  return options?.logger ? timezone.resolveTimezone(location, { logger: options.logger }) : timezone.resolveTimezone(location);
}

// src/geo/providers/local/timezone.ts
import tzlookup from "tz-lookup";
import { getLogger as getLogger3 } from "@hopdrive/sdk-core";
var LocalTimezoneProvider = class {
  constructor() {
    this.name = "local_tzlookup";
  }
  async resolveTimezone(location, options) {
    const log = options?.logger ?? getLogger3();
    try {
      const timeZoneId = tzlookup(location.latitude, location.longitude);
      if (!timeZoneId) return null;
      return {
        timeZoneId,
        timeZoneName: timeZoneId,
        rawOffsetSeconds: 0,
        dstOffsetSeconds: 0
      };
    } catch (error) {
      log.warn("timezone.localResolutionError", {
        error,
        latitude: location.latitude,
        longitude: location.longitude
      });
      return null;
    }
  }
};

// src/geo/index.ts
function createDefaultProviders(config) {
  const apiKey = typeof config === "string" ? config : config.apiKey;
  const clientConfig = typeof config === "string" ? {} : config.clientConfig;
  const client = new GoogleClient({
    apiKey,
    ...clientConfig
  });
  return {
    geocoding: new GoogleGeocodingProvider(client),
    routing: new GoogleRoutesProvider(client),
    suggestion: new GoogleSuggestionProvider(client),
    timezone: new LocalTimezoneProvider(),
    client
  };
}
function createGoogleProviders(config) {
  const apiKey = typeof config === "string" ? config : config.apiKey;
  const clientConfig = typeof config === "string" ? {} : config.clientConfig;
  const client = new GoogleClient({
    apiKey,
    ...clientConfig
  });
  return {
    geocoding: new GoogleGeocodingProvider(client),
    routing: new GoogleRoutesProvider(client),
    suggestion: new GoogleSuggestionProvider(client),
    timezone: new GoogleTimezoneProvider(client),
    client
  };
}
function createGoogleSuggester(config) {
  const apiKey = typeof config === "string" ? config : config.apiKey;
  const clientConfig = typeof config === "string" ? {} : config.clientConfig;
  const client = new GoogleClient({
    apiKey,
    ...clientConfig
  });
  return new GoogleSuggestionProvider(client);
}
function createGoogleGeocoder(config) {
  const apiKey = typeof config === "string" ? config : config.apiKey;
  const clientConfig = typeof config === "string" ? {} : config.clientConfig;
  const client = new GoogleClient({
    apiKey,
    ...clientConfig
  });
  return new GoogleGeocodingProvider(client);
}
function createGoogleRouter(config) {
  const apiKey = typeof config === "string" ? config : config.apiKey;
  const clientConfig = typeof config === "string" ? {} : config.clientConfig;
  const client = new GoogleClient({
    apiKey,
    ...clientConfig
  });
  return new GoogleRoutesProvider(client);
}

// src/init.ts
import {
  createFetchExecutor as createFetchExecutor2,
  resolveExecutor,
  resetDefaultExecutor,
  getLogger as getLogger4
} from "@hopdrive/sdk-core";
var defaultGeoProviders = null;
var googleApiKey = null;
function init(config) {
  if (!config.googleApiKey) {
    throw new Error(
      'sdk-lanes requires googleApiKey. Provide it in init({ googleApiKey: "..." })'
    );
  }
  if (config.executor && config.executorConfig) {
    throw new Error(
      'Provide either "executor" or "executorConfig", not both.'
    );
  }
  if (!config.executor && !config.executorConfig) {
    throw new Error(
      'Must provide either "executor" (pre-created) or "executorConfig" (to create one).'
    );
  }
  const executor = config.executor ?? createFetchExecutor(config.executorConfig);
  coreInit({ executor, logger: config.logger });
  googleApiKey = config.googleApiKey;
  const providers = createDefaultProviders(config.googleApiKey);
  defaultGeoProviders = {
    geocoding: providers.geocoding,
    routing: providers.routing,
    suggestion: providers.suggestion,
    timezone: providers.timezone
  };
}
function getDefaultGeoProviders() {
  if (!defaultGeoProviders) {
    throw new Error(
      "sdk-lanes not initialized. Call init() first with executor and googleApiKey."
    );
  }
  return defaultGeoProviders;
}
function getGoogleApiKey() {
  if (!googleApiKey) {
    throw new Error(
      "sdk-lanes not initialized. Call init() first with executor and googleApiKey."
    );
  }
  return googleApiKey;
}
function isInitialized() {
  return defaultGeoProviders !== null;
}
function resetLanesConfig() {
  defaultGeoProviders = null;
  googleApiKey = null;
}

export {
  GeoError,
  GeocodingError,
  RoutingError,
  SuggestionError,
  GeoApiError,
  GeoRateLimitError,
  GoogleClient,
  GoogleGeocodingProvider,
  GoogleRoutesProvider,
  GoogleSuggestionProvider,
  GoogleTimezoneProvider,
  resolveTimezone,
  LocalTimezoneProvider,
  createDefaultProviders,
  createGoogleProviders,
  createGoogleSuggester,
  createGoogleGeocoder,
  createGoogleRouter,
  init,
  getDefaultGeoProviders,
  getGoogleApiKey,
  isInitialized,
  resetLanesConfig,
  createFetchExecutor2 as createFetchExecutor,
  resolveExecutor,
  resetDefaultExecutor,
  getLogger4 as getLogger
};
//# sourceMappingURL=chunk-X4N6ZUIX.mjs.map